<template>
  <ModuleWrapper :moduleId="moduleId" title="Stage Navigation" @initializeData="initializeData">
    <template #main>
      <div>
        <StudioToggleWithLabel
          v-if="!isWebinar"
          id="rooms-tab-toggle"
          label="Rooms Tab"
          subLabel="If your event has rooms, showing the tab allows users view or enter your rooms at any time during the event."
          class="tw-mb-4"
          :checked="roomsTabEnabled"
          @update:checked="handleRoomsTabToggle"
        />
        <StudioInput
          v-if="roomsTabEnabled && !isWebinar"
          id="custom-rooms-tab-title-input"
          label="Rooms Tab Title"
          class="tw-mb-7"
          :maxlength="50"
          :modelValue="roomsTabTitle"
          @update:modelValue="value => editRoomsTabTitle(value)"
        />
      </div>
      <template v-if="isBoothsTabEnabled">
        <div>
          <StudioToggleWithLabel
            v-if="!isWebinar"
            id="booths-tab-toggle"
            label="Booths Tab"
            subLabel="If your event has booths, showing the tab allows users view or enter your booths at any time during the event."
            class="tw-mb-4"
            :checked="boothsTabEnabled"
            @update:checked="handleBoothsTabToggle"
          />
          <StudioInput
            v-if="boothsTabEnabled && !isWebinar"
            id="custom-booths-tab-title-input"
            label="Booths Tab Title"
            class="tw-mb-7"
            required
            :maxlength="50"
            :modelValue="boothsTabTitle"
            :inlineError="!boothsTabTitle ? 'This field is required' : ''"
            @update:modelValue="value => editBoothsTabTitle(value)"
          />
        </div>
        <template v-if="boothsTabEnabled">
          <StudioToggleWithLabel
            v-if="!isWebinar"
            id="secondary-booths-tab-toggle"
            label="Secondary Booths Tab"
            subLabel="Create a second, separate Booths tab inside your event. This can be useful for separating different types of booths (your company's vs. your sponsors', for example)."
            class="tw-mb-4"
            :checked="secondaryBoothsTabEnabled"
            @update:checked="handleSecondaryBoothsTabToggle"
          />
          <StudioInput
            v-if="secondaryBoothsTabEnabled && !isWebinar"
            id="custom-secondary-booths-tab-title-input"
            label="Secondary Booths Tab Title"
            class="tw-mb-7"
            :maxlength="50"
            :modelValue="secondaryBoothsTabTitle"
            @update:modelValue="value => editSecondaryBoothsTabTitle(value)"
          />
        </template>
      </template>
      <div>
        <StudioToggleWithLabel
          id="speakers-tab-toggle"
          label="Speakers Tab"
          subLabel="Let your audience see the list of all speakers in your event."
          class="tw-mb-4"
          :checked="speakersTabEnabled"
          @update:checked="handleSpeakersTabToggle"
        />
        <template v-if="speakersTabEnabled">
          <StudioInput
            id="speakers-tab-title-input"
            label="Speakers Tab Title"
            class="tw-mb-4"
            :maxlength="50"
            :modelValue="speakersTabTitle"
            @update:modelValue="value => editSpeakersTabTitle(value)"
          />
          <StudioTextarea
            v-if="isWebinar"
            id="speaker-tab-description"
            class="tw-mb-7"
            :modelValue="speakerTabDescription"
            label="Speaker Tab Description"
            :maxlength="137"
            @update:modelValue="description => editSpeakersTabDescription(description)"
          />
        </template>
      </div>
      <div>
        <StudioToggleWithLabel
          id="attendees-tab-toggle"
          label="Attendees Tab"
          subLabel="When the Attendees tab is turned on, attendees can view each other’s names, photos, title, company, and event status (currently attending, not attending). This tab is hidden in on-demand mode."
          class="tw-mb-4"
          :checked="attendeesTabEnabled"
          @update:checked="handleAttendeesTabToggle"
        />
        <Transition name="accordion">
          <StudioToggleWithLabel
            v-show="!attendeesTabEnabled"
            id="attendees-tab-enabled-for-speakers-toggle"
            label="Attendees Tab for Speakers"
            subLabel="If enabled, only speakers will be able to see the Attendees tab in the event. Speakers can view all attendee names, photos, title, company, and event status (currently attending, not attending)."
            class="tw-mb-4"
            :checked="attendeesTabEnabledForSpeakers"
            @update:checked="handleAttendeesTabEnabledForSpeakersToggle"
          />
        </Transition>
        <StudioInput
          v-if="attendeesTabEnabled || attendeesTabEnabledForSpeakers"
          id="attendees-tab-title-input"
          label="Attendees Tab Title"
          :maxlength="50"
          class="tw-mb-4"
          :modelValue="attendeesTabTitle"
          @update:modelValue="value => editAttendeesTabTitle(value)"
        />
      </div>
      <div v-if="!isWebinar">
        <StudioToggleWithLabel
          id="attendees-profile-toggle"
          label="Attendee Profiles"
          subLabel="If enabled, attendee's name will display their Profile information, which may include their name, profile picture, company, title, location, Linkedin, X (twitter)"
          class="tw-mb-4"
          :checked="attendeesProfileEnabled"
          @update:checked="handleAttendeesProfileToggle"
        />
      </div>
    </template>
  </ModuleWrapper>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import ModuleWrapper from './ModuleWrapper.vue';

import { StudioInput, StudioToggleWithLabel } from '@/studioComponents';
import StudioTextarea from '@/studioComponents/StudioTextarea.vue';

export default defineComponent({
  name: 'StageExperience',
  components: {
    ModuleWrapper,
    StudioToggleWithLabel,
    StudioInput,
    StudioTextarea
  },
  props: {
    moduleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      roomsTabEnabled: false,
      roomsTabTitle: '',
      boothsTabEnabled: false,
      boothsTabTitle: '',
      secondaryBoothsTabEnabled: false,
      secondaryBoothsTabTitle: '',
      speakersTabEnabled: false,
      speakersTabTitle: '',
      attendeesTabEnabled: false,
      attendeesTabTitle: '',
      attendeesTabEnabledForSpeakers: false,
      speakerTabDescription: '',
      attendeesProfileEnabled: false
    };
  },
  mounted() {
    this.initializeData();
  },
  computed: {
    ...mapGetters('branding', {
      getInitialModuleData: 'getInitialModuleData'
    }),
    ...mapGetters('studioEventList', {
      event: 'getCurrentEvent'
    }),
    ...mapGetters('orgPricingConfig', {
      isBoothsTabEnabled: 'getIsBoothsTabEnabled'
    }),
    isWebinar() {
      return this.event?.event_type === 'Webinar';
    },
    isFormValid() {
      if (this.roomsTabEnabled && !this.roomsTabTitle) {
        return false;
      }

      if (this.boothsTabEnabled && !this.boothsTabTitle) {
        return false;
      }

      return true;
    }
  },
  watch: {
    isFormValid(isValid) {
      this.setCurrentModuleInvalid(!isValid);
    }
  },
  methods: {
    ...mapActions('branding', ['onSelectModule']),
    ...mapMutations('branding', {
      setChangesMadeInEvent: 'setChangesMadeInEvent',
      setCurrentModuleInvalid: 'setCurrentModuleInvalid'
    }),
    initializeData() {
      const moduleKeys = [
        'show_discussion_rooms',
        'discussion_room_tab_title',
        'show_event_booths',
        'booth_tab_title',
        'booth_tab_title_2',
        'show_attendees',
        'people_tab_title',
        'show_people_tab_speakers',
        'speaker_tab_visible',
        'speaker_tab_title',
        'tab_descriptions',
        'show_attendee_profiles'
      ];

      const {
        show_discussion_rooms,
        discussion_room_tab_title,
        show_event_booths,
        booth_tab_title,
        booth_tab_title_2,
        show_attendees,
        people_tab_title,
        show_people_tab_speakers,
        speaker_tab_visible,
        speaker_tab_title,
        tab_descriptions,
        show_attendee_profiles
      } = this.getInitialModuleData(moduleKeys);

      this.roomsTabEnabled = show_discussion_rooms;
      this.roomsTabTitle = discussion_room_tab_title || 'Rooms';
      this.boothsTabEnabled = this.isBoothsTabEnabled && show_event_booths;
      this.boothsTabTitle = booth_tab_title || 'Booths';
      this.secondaryBoothsTabEnabled = !!booth_tab_title_2;
      this.secondaryBoothsTabTitle = booth_tab_title_2 || 'Secondary Booths';
      this.speakersTabEnabled = speaker_tab_visible;
      this.speakersTabTitle = speaker_tab_title || 'Speakers';
      this.attendeesTabEnabled = show_attendees;
      this.attendeesTabTitle = people_tab_title || 'People';
      this.attendeesTabEnabledForSpeakers = show_people_tab_speakers;
      this.speakerTabDescription = tab_descriptions?.speakers || '';
      this.attendeesProfileEnabled = show_attendee_profiles;
    },
    handleRoomsTabToggle(e) {
      this.roomsTabEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        show_discussion_rooms: this.roomsTabEnabled
      });
    },
    editRoomsTabTitle(text: string) {
      this.roomsTabTitle = text;
      this.setChangesMadeInEvent({
        discussion_room_tab_title: this.roomsTabTitle
      });
    },
    handleBoothsTabToggle(e) {
      this.boothsTabEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        show_event_booths: this.boothsTabEnabled
      });
    },
    editBoothsTabTitle(text: string) {
      this.boothsTabTitle = text;
      this.setChangesMadeInEvent({
        booth_tab_title: this.boothsTabTitle
      });
    },
    handleSecondaryBoothsTabToggle(e) {
      this.secondaryBoothsTabEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        booth_tab_title_2: this.secondaryBoothsTabEnabled ? this.secondaryBoothsTabTitle : null
      });
    },
    editSecondaryBoothsTabTitle(text: string) {
      this.secondaryBoothsTabTitle = text;
      this.setChangesMadeInEvent({
        booth_tab_title_2: this.secondaryBoothsTabTitle
      });
    },
    handleSpeakersTabToggle(e) {
      this.speakersTabEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        speaker_tab_visible: this.speakersTabEnabled
      });
    },
    editSpeakersTabTitle(text: string) {
      this.speakersTabTitle = text;
      this.setChangesMadeInEvent({
        speaker_tab_title: this.speakersTabTitle
      });
    },
    editSpeakersTabDescription(text: string) {
      this.speakerTabDescription = text;
      this.setChangesMadeInEvent({
        tab_descriptions: {
          ...this.event.tab_descriptions,
          speakers: this.speakerTabDescription
        }
      });
    },
    handleAttendeesTabToggle(e) {
      this.attendeesTabEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        show_attendees: this.attendeesTabEnabled
      });
    },
    editAttendeesTabTitle(text: string) {
      this.attendeesTabTitle = text;
      this.setChangesMadeInEvent({
        people_tab_title: this.attendeesTabTitle
      });
    },
    handleAttendeesTabEnabledForSpeakersToggle(e) {
      this.attendeesTabEnabledForSpeakers = e.target.checked;
      this.setChangesMadeInEvent({
        show_people_tab_speakers: this.attendeesTabEnabledForSpeakers
      });
    },
    handleAttendeesProfileToggle(e) {
      this.attendeesProfileEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        show_attendee_profiles: this.attendeesProfileEnabled
      });
    }
  }
});
</script>

<style lang="scss" scoped>
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s;
  overflow: hidden;
  max-height: 100px;
}

.accordion-enter-from,
.accordion-leave-to {
  max-height: 0px;
}
</style>
